/**
 * Customer 360 (owner product review §26–35). Compact sticky header (identity + status chips + one
 * «عملیات مدیریت ▾» control) and URL-addressable tabs. Money tabs render only when the financial read
 * model has an authoritative source (none before the ledger phases; a labelled review fixture in dev).
 * All OD-38 actions keep step-up + reason + confirmation (AdminControls.tsx). Values arrive masked;
 * the Full Admin reveals complete values on demand (step-up, audited per reveal — OD-41, Reveal.tsx).
 */
import {
  ActionMenu,
  Amount,
  AppContainer,
  Badge,
  Card,
  cx,
  DataTable,
  formatAmount,
  formatRelative,
  KeyValueGrid,
  KpiTile,
  PageTabs,
  StateView,
  StatusBadge,
  type PageTab,
} from '@hopex/ui';
import { Link } from '@tanstack/react-router';
import { ArrowRight, FlaskConical, ShieldAlert, Wallet } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { dt, type AdminUserDetail } from '../api.ts';
import {
  TX_KIND,
  useCustomerFinance,
  type CustomerFinanceState,
  type CustomerFinanceView,
  type TxRow,
} from '../lib/finance.ts';
import {
  ACCOUNT_STATUS,
  ACTOR,
  auditLabel,
  CHECK_LABEL,
  CHECK_STATE,
  eventLabel,
  KYC1_STATUS,
  LEVEL_LABEL,
  CORRECTION_STATUS,
  EVIDENCE_TYPE,
  outcomeLabel,
  OWNERSHIP,
  reasonLabel,
  SEVERITY,
  SOURCE_LABEL,
} from '../lib/labels.ts';
import { Remote, useRemote } from '../lib/remote.tsx';
import { BankName, Labeled, TechCode } from '../lib/ui.tsx';
import { IdentityReveal, InstrumentReveal } from './Reveal.tsx';
import { Kyc2Attempts } from './Kyc2Attempts.tsx';
import { AdjustmentDialog } from './money/AdjustmentDialog.tsx';
import { JournalList, MoneyOpsTab, TomanTab } from './money/CustomerMoney.tsx';
import { CryptoTab, SupportTab, TradesTab } from './c360/LiveTabs.tsx';
import { AdminActionMenu, buildActions, levelSummary, Note } from './AdminControls.tsx';

const NOT_ACTIVE = 'ماژول مالی هنوز فعال نشده است';

function Section({
  title,
  action,
  children,
  className,
  ...rest
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
} & Record<`data-${string}`, unknown>) {
  return (
    <Card className={cx('p-4 sm:p-5', className)} {...rest}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="m-0 text-[14px] font-bold text-fg">{title}</h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

const money = (v: string | null | undefined, unit = 'TOMAN') =>
  v === null || v === undefined ? null : <Amount value={v} unit={unit} />;

function Chip({
  tone,
  children,
  ...rest
}: { tone: 'positive' | 'negative' | 'accent' | 'neutral'; children: ReactNode } & Record<
  `data-${string}`,
  unknown
>) {
  return (
    <span {...rest}>
      <Badge tone={tone}>{children}</Badge>
    </span>
  );
}

/* ───────── header ───────── */

function Header({
  d,
  f,
  onChanged,
}: {
  d: AdminUserDetail;
  f: CustomerFinanceState;
  onChanged: () => void;
}) {
  const a = buildActions(d);
  const [adjusting, setAdjusting] = useState(false);
  const userLabel = `${d.user.displayName ?? 'بدون نام'} (${d.user.publicId})`;
  const risk =
    d.user.status !== 'ACTIVE' ||
    d.securityEvents.some((e) => e.severity === 'CRITICAL') ||
    f.riskFlags.length > 0;
  return (
    <div
      className="sticky top-16 z-20 -mx-4 mb-4 border-b border-line bg-canvas/95 px-4 py-3 backdrop-blur-md md:-mx-6 md:px-6 xl:-mx-8 xl:px-8"
      data-c360-header
    >
      <Link
        to="/users"
        className="mb-1.5 inline-flex items-center gap-1 text-[12px] text-fg-muted hover:text-fg"
      >
        <ArrowRight aria-hidden className="size-3.5" />
        کاربران
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            aria-hidden
            className="grid size-11 shrink-0 place-items-center rounded-full bg-surface-3 text-[16px] font-bold text-fg"
          >
            {d.user.displayName?.trim().charAt(0) ?? '؟'}
          </span>
          <div className="min-w-0">
            <h1 className="m-0 truncate text-[19px] font-extrabold leading-tight">
              {d.user.displayName ?? 'کاربر بدون نام'}
            </h1>
            <p className="m-0 mt-0.5 flex flex-wrap items-center gap-x-2 text-[12px] text-fg-muted">
              <span>
                شناسه‌ی هوپکس{' '}
                <bdi dir="ltr" className="font-mono font-semibold text-fg" data-public-id>
                  {d.user.publicId}
                </bdi>
              </span>
              <span aria-hidden>·</span>
              <bdi dir="ltr" className="font-mono">
                {d.user.phoneMasked ?? '—'}
              </bdi>
            </p>
          </div>
          <div className="ms-1 flex flex-wrap items-center gap-1.5" data-c360-chips>
            <Chip tone={d.user.status === 'ACTIVE' ? 'positive' : 'negative'} data-chip-status>
              {ACCOUNT_STATUS[d.user.status] ?? d.user.status}
            </Chip>
            <Chip tone={d.effectiveLevel === 'NONE' ? 'neutral' : 'positive'} data-chip-kyc>
              {levelSummary(d)}
            </Chip>
            <Chip
              tone={
                d.security.totpEnabled ? 'positive' : d.security.totpRequired ? 'accent' : 'neutral'
              }
            >
              {d.security.totpEnabled
                ? 'دومرحله‌ای فعال'
                : d.security.totpRequired
                  ? 'دومرحله‌ای اجباری'
                  : 'دومرحله‌ای غیرفعال'}
            </Chip>
            {risk && (
              <span className="inline-flex items-center gap-1 rounded-full bg-negative-tint px-2 py-0.5 text-[11px] font-semibold text-negative">
                <ShieldAlert aria-hidden className="size-3.5" />
                نیازمند توجه
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2" data-admin-controls>
          {f.financial.source === 'REVIEW_FIXTURE' && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-accent-soft px-2.5 py-1 text-[11px] font-semibold text-accent-text"
              data-review-fixture
            >
              <FlaskConical aria-hidden className="size-3.5" />
              داده‌ی مالی نمونه‌ی بازبینی
            </span>
          )}
          {f.ledger && (
            <ActionMenu
              label="عملیات مالی"
              variant="secondary"
              data-finance-menu
              sections={[
                {
                  title: 'دفتر کل',
                  items: [
                    {
                      id: 'ADJUST',
                      label: 'اصلاح موجودی',
                      tone: 'caution',
                      description: 'افزایش، کاهش یا مسدودسازی با سند دوطرفه',
                    },
                  ],
                },
              ]}
              onAction={() => setAdjusting(true)}
            />
          )}
          {adjusting && (
            <AdjustmentDialog
              userId={d.user.id}
              userLabel={userLabel}
              initial={null}
              onClose={() => setAdjusting(false)}
              onDone={() => {
                setAdjusting(false);
                f.reload();
                onChanged();
              }}
            />
          )}
          <AdminActionMenu
            d={d}
            onChanged={onChanged}
            label="عملیات مدیریت"
            data-admin-menu
            sections={[
              { title: 'مدیریت احراز', specs: a.kyc },
              { title: 'مدیریت هویت', specs: a.identity },
              { title: 'مدیریت امنیت', specs: a.security },
              { title: 'مدیریت حساب', specs: a.account },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

/* ───────── tabs ───────── */

/**
 * Financial overview with an operator-first hierarchy (final QA §7): six primary metrics lead, the
 * remaining fields sit in labelled groups (current · lifetime crypto flows · trading · activity).
 * Every field of the contract stays visible.
 */
function FinancialKpis({ f }: { f: CustomerFinanceView }) {
  const fin = f.financial;
  const count = (v: number | null) =>
    v === null ? null : <bdi className="hx-num">{v.toLocaleString('en-US')}</bdi>;
  const small = (label: string, v: ReactNode | null) => (
    <KpiTile key={label} label={label} value={v} size="sm" data-financial-kpi="secondary" />
  );
  // Columns come from a minimum tile width, not a fixed count: at narrower widths the grid drops
  // columns instead of squeezing, so long amounts never run into the neighbouring tile.
  const group = (title: string, tiles: ReactNode[]) => (
    <section aria-label={title} className="flex flex-col gap-2" data-financial-group>
      <h3 className="m-0 text-[12px] font-semibold text-fg-muted">{title}</h3>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-2">{tiles}</div>
    </section>
  );
  // No authoritative source yet (before the ledger phases): one compact, honest panel instead of a
  // grid of «—» tiles. Never a zero. LEDGER / REVIEW_FIXTURE keep the full hierarchy below.
  if (fin.source === 'NONE')
    return (
      <div
        className="flex items-start gap-3 rounded-md border border-line bg-surface-1 p-4"
        data-financial-overview="NONE"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-full border border-line text-fg-muted">
          <Wallet aria-hidden className="size-[18px]" />
        </span>
        <div className="flex flex-col gap-1">
          <p className="m-0 text-[14px] font-semibold text-fg">اطلاعات مالی هنوز در دسترس نیست.</p>
          <p className="m-0 text-[13px] leading-7 text-fg-muted">
            پس از فعال‌شدن ماژول مالی، موجودی، واریزها، برداشت‌ها و گردش این کاربر اینجا نمایش داده
            می‌شود.
          </p>
        </div>
      </div>
    );
  return (
    <div className="flex flex-col gap-4" data-financial-overview={fin.source}>
      {/* Max four primary metrics per row (1440); 1280/1024 drop to fewer columns. */}
      <div
        className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-3"
        data-financial-primary
      >
        {(
          [
            ['ارزش کل دارایی', money(fin.current.portfolioToman)],
            ['موجودی تومان', money(fin.current.tomanBalance)],
            ['کل واریز تومان', money(fin.lifetime.tomanDeposits)],
            ['کل برداشت تومان', money(fin.lifetime.tomanWithdrawals)],
          ] as const
        ).map(([label, v]) => (
          <KpiTile key={label} label={label} value={v} data-financial-kpi="primary" />
        ))}
      </div>
      <div className="flex flex-col gap-4">
        {group('جریان تومان', [
          small('خالص جریان تومان', money(fin.lifetime.netTomanFlow)),
          small('تعداد تراکنش', count(fin.lifetime.transactionCount)),
        ])}
        {group('دارایی فعلی', [
          small('ارزش تتری', money(fin.current.usdtValue, 'USDT')),
          small('قابل استفاده', money(fin.current.availableToman)),
          small('رزروشده', money(fin.current.reservedToman)),
        ])}
        {group('جریان رمزارز (ارزش تومانی)', [
          small('واریز رمزارز', money(fin.lifetime.cryptoDepositsToman)),
          small('برداشت رمزارز', money(fin.lifetime.cryptoWithdrawalsToman)),
        ])}
        {group('معاملات', [
          small('حجم خرید', money(fin.lifetime.buyVolumeToman)),
          small('حجم فروش', money(fin.lifetime.sellVolumeToman)),
          small('حجم تبدیل', money(fin.lifetime.convertVolumeToman)),
          small('کارمزد پرداختی', money(fin.lifetime.feesPaidToman)),
        ])}
        {group('فعالیت', [
          small(
            'آخرین تراکنش',
            fin.recent.lastTransactionAt ? formatRelative(fin.recent.lastTransactionAt) : null,
          ),
          small('تراکنش‌های در انتظار', count(fin.recent.pendingCount)),
          small('مبلغ در انتظار', money(fin.recent.pendingAmountToman)),
        ])}
      </div>
    </div>
  );
}

function TxTable({ rows, label }: { rows: TxRow[]; label: string }) {
  return (
    <DataTable<TxRow>
      label={label}
      rows={rows}
      density="compact"
      stickyFirst
      columns={[
        { id: 'type', header: 'نوع', isRowHeader: true, cell: (t) => TX_KIND[t.type] },
        { id: 'asset', header: 'دارایی', cell: (t) => (t.asset === 'TOMAN' ? 'تومان' : t.asset) },
        {
          id: 'amount',
          header: 'مقدار',
          numeric: true,
          cell: (t) => formatAmount(t.amount, t.asset),
        },
        {
          id: 'toman',
          header: 'ارزش تومانی',
          numeric: true,
          cell: (t) => formatAmount(t.tomanValue, 'TOMAN'),
        },
        {
          id: 'fee',
          header: 'کارمزد',
          numeric: true,
          cell: (t) =>
            t.fee
              ? formatAmount(t.fee, t.type === 'BUY' || t.type === 'SELL' ? 'TOMAN' : t.asset)
              : '—',
        },
        {
          id: 'status',
          header: 'وضعیت',
          cell: (t) => (
            <StatusBadge status={t.status} {...(t.statusLabel ? { label: t.statusLabel } : {})} />
          ),
        },
        {
          id: 'ref',
          header: 'کد پیگیری',
          cell: (t) => (
            <bdi dir="ltr" className="font-mono text-[12px]">
              {t.reference}
            </bdi>
          ),
        },
        { id: 'at', header: 'زمان', cell: (t) => dt(t.at) },
      ]}
      empty={<StateView compact kind="empty" title="تراکنشی ثبت نشده است" />}
    />
  );
}

function OverviewTab({ d, f }: { d: AdminUserDetail; f: CustomerFinanceView }) {
  const lastRisk = d.securityEvents.find((e) => e.severity !== 'INFO');
  const lastDecision = d.decisions[0];
  return (
    <div className="grid gap-4 xl:grid-cols-3" data-tab-overview>
      <Section title="خلاصه‌ی مشتری" className="xl:col-span-2">
        <KeyValueGrid
          columns={3}
          items={[
            {
              label: 'شناسه‌ی هوپکس',
              value: (
                <bdi dir="ltr" className="font-mono">
                  {d.user.publicId}
                </bdi>
              ),
            },
            {
              label: 'موبایل',
              value: (
                <bdi dir="ltr" className="font-mono">
                  {d.user.phoneMasked ?? '—'}
                </bdi>
              ),
            },
            {
              label: 'منبع موبایل',
              value: d.security.phoneSource === 'ADMIN' ? 'ثبت مدیر' : 'کد پیامکی',
            },
            { label: 'تاریخ ثبت‌نام', value: dt(d.user.createdAt) },
            {
              label: 'آخرین فعالیت',
              value: d.user.lastActiveAt ? formatRelative(d.user.lastActiveAt) : '—',
            },
            { label: 'آخرین ورود', value: dt(d.security.lastLoginAt) },
          ]}
        />
        <IdentityReveal userId={d.user.id} />
        <details className="mt-3 border-t border-line pt-2 text-[12px]" data-technical-ids>
          <summary className="cursor-pointer text-fg-muted">جزئیات فنی</summary>
          <p className="m-0 mt-1.5 text-fg-muted">
            شناسه‌ی داخلی (UUID، فقط برای پیگیری فنی):{' '}
            <bdi dir="ltr" className="font-mono text-fg" data-user-id>
              {d.user.id}
            </bdi>
          </p>
        </details>
      </Section>
      <Section title="وضعیت حساب، احراز و امنیت">
        <KeyValueGrid
          columns={1}
          items={[
            { label: 'وضعیت حساب', value: ACCOUNT_STATUS[d.user.status] ?? d.user.status },
            { label: 'سطح مؤثر احراز', value: levelSummary(d) },
            {
              label: 'ورود دومرحله‌ای',
              value: d.security.totpEnabled
                ? 'فعال'
                : d.security.totpRequired
                  ? 'راه‌اندازی اجباری'
                  : 'غیرفعال',
            },
            {
              label: 'کارت/شبای تأییدشده',
              value: d.bankInstruments
                .filter((b) => b.ownershipStatus === 'VERIFIED' && b.active)
                .length.toLocaleString('fa-IR'),
            },
          ]}
        />
      </Section>
      <Section title="نمای مالی" className="xl:col-span-3">
        <FinancialKpis f={f} />
      </Section>
      <Section title="آخرین تراکنش‌ها" className="xl:col-span-2">
        {f.transactions ? (
          <TxTable rows={f.transactions.slice(0, 5)} label="آخرین تراکنش‌ها" />
        ) : (
          <StateView
            compact
            kind="unavailable"
            title="تراکنشی برای نمایش نیست"
            description={NOT_ACTIVE}
          />
        )}
      </Section>
      <div className="flex flex-col gap-4">
        <Section title="آخرین ریسک / رویداد امنیتی">
          {f.riskFlags.length > 0 && (
            <ul className="m-0 mb-2 list-none p-0 text-[13px] text-negative">
              {f.riskFlags.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
          {lastRisk ? (
            <p className="m-0 text-[13px]">
              {eventLabel(lastRisk.type)} · <span className="text-fg-muted">{dt(lastRisk.at)}</span>
            </p>
          ) : (
            <p className="m-0 text-[13px] text-fg-muted">رویداد پرخطری ثبت نشده است.</p>
          )}
        </Section>
        <Section title="آخرین تصمیم مدیریتی">
          {lastDecision ? (
            <p className="m-0 text-[13px] leading-6">
              {auditLabel(lastDecision.action)}
              <span className="block text-[12px] text-fg-muted">
                {lastDecision.reason} · <bdi dir="ltr">{lastDecision.admin}</bdi> ·{' '}
                {dt(lastDecision.at)}
              </span>
            </p>
          ) : (
            <p className="m-0 text-[13px] text-fg-muted">تصمیمی ثبت نشده است.</p>
          )}
        </Section>
      </div>
    </div>
  );
}

function WalletTab({ f }: { f: CustomerFinanceView }) {
  return (
    <div className="flex flex-col gap-4" data-tab-wallets>
      <FinancialKpis f={f} />
      <Section title="کیف‌ها و موجودی">
        <DataTable
          label="کیف‌ها و موجودی"
          rows={f.wallets ?? []}
          density="compact"
          stickyFirst
          columns={[
            {
              id: 'asset',
              header: 'دارایی',
              isRowHeader: true,
              cell: (w) => (w.asset === 'TOMAN' ? 'تومان' : w.asset),
            },
            {
              id: 'total',
              header: 'کل',
              numeric: true,
              cell: (w) => formatAmount(w.total, w.asset, { withUnit: false }),
            },
            {
              id: 'available',
              header: 'قابل استفاده',
              numeric: true,
              cell: (w) => formatAmount(w.available, w.asset, { withUnit: false }),
            },
            {
              id: 'reserved',
              header: 'رزروشده',
              numeric: true,
              cell: (w) => formatAmount(w.reserved, w.asset, { withUnit: false }),
            },
            {
              id: 'toman',
              header: 'ارزش تومانی',
              numeric: true,
              cell: (w) => formatAmount(w.tomanValue, 'TOMAN', { withUnit: false }),
            },
          ]}
          empty={<StateView compact kind="empty" title="کیفی ثبت نشده است" />}
        />
      </Section>
    </div>
  );
}

function KycTab({ d, onChanged }: { d: AdminUserDetail; onChanged: () => void }) {
  const a = buildActions(d);
  return (
    <div className="grid gap-4 xl:grid-cols-2" data-tab-kyc>
      <Section
        title="احراز هویت"
        className="xl:col-span-2"
        action={
          <AdminActionMenu
            d={d}
            onChanged={onChanged}
            label="عملیات احراز"
            variant="secondary"
            size="sm"
            data-kyc-menu
            sections={[
              { title: 'سطح ۱', specs: a.kyc.filter((s) => s.key.startsWith('KYC1')) },
              { title: 'سطح ۲', specs: a.kyc.filter((s) => s.key.startsWith('KYC2')) },
              { title: 'تعیین مستقیم سطح', specs: a.kyc.filter((s) => s.key.startsWith('SET_')) },
              { title: 'هویت', specs: a.identity },
            ]}
          />
        }
      >
        <KeyValueGrid
          columns={4}
          items={[
            { label: 'سطح مؤثر', value: LEVEL_LABEL[d.effectiveLevel] ?? d.effectiveLevel },
            {
              label: 'سطح ۱',
              value: `${KYC1_STATUS[d.kyc?.status ?? 'NOT_STARTED'] ?? '—'} · ${SOURCE_LABEL(d.kyc?.verificationSource)}`,
            },
            {
              label: 'سطح ۲',
              value: `${KYC1_STATUS[d.kyc2.status] ?? d.kyc2.status} · ${SOURCE_LABEL(d.kyc2.verificationSource)}`,
            },
            {
              label: 'علت آخرین نتیجه',
              value: d.kyc?.reason ? (
                <Labeled label={reasonLabel(d.kyc.reason)} code={d.kyc.reason} />
              ) : (
                '—'
              ),
            },
            ...(d.kyc?.identity
              ? [
                  { label: 'نام قانونی', value: d.kyc.identity.legalName },
                  {
                    label: 'کد ملی',
                    value: (
                      <bdi dir="ltr" className="font-mono">
                        {d.kyc.identity.nationalIdMasked}
                      </bdi>
                    ),
                  },
                  {
                    label: 'سال تولد (میلادی)',
                    value: <bdi className="hx-num">{d.kyc.identity.birthYear}</bdi>,
                  },
                  {
                    label: 'نسخه‌ی هویت',
                    value: <bdi className="hx-num">{d.kyc.identityVersion}</bdi>,
                  },
                ]
              : []),
          ]}
        />
        <div className="mt-3">
          <Note>
            نتیجه‌ی استعلام‌های رسمی همان‌طور که ثبت شده باقی می‌ماند. تصمیم مدیر با منبع «تصمیم
            مدیر» جداگانه ثبت می‌شود و سابقه پاک نمی‌شود.
          </Note>
        </div>
      </Section>
      <Section title="بررسی‌های سطح ۱">
        {d.kyc ? (
          <ul className="m-0 list-none divide-y divide-line p-0">
            {Object.entries(d.kyc.checks).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between gap-2 py-2 text-[13px]">
                <span>{CHECK_LABEL[k] ?? k}</span>
                <Badge tone={CHECK_STATE[v]?.tone ?? 'neutral'}>{CHECK_STATE[v]?.label ?? v}</Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 text-[13px] text-fg-muted">کاربر احراز هویت را شروع نکرده است.</p>
        )}
      </Section>
      <Section title="نسخه‌های هویت تأییدشده" data-identity-versions>
        {d.identityVersions.length === 0 ? (
          <p className="m-0 text-[13px] text-fg-muted">نسخه‌ی تأییدشده‌ای وجود ندارد.</p>
        ) : (
          <ul className="m-0 list-none divide-y divide-line p-0 text-[13px]">
            {d.identityVersions.map((v) => (
              <li
                key={v.version}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <span>
                  نسخه‌ی {v.version} · {v.legalName} ·{' '}
                  <bdi dir="ltr" className="font-mono">
                    {v.nationalIdMasked}
                  </bdi>
                </span>
                <span className="flex items-center gap-2 text-fg-muted">
                  <Badge tone={v.current ? 'positive' : 'neutral'}>
                    {v.current ? 'فعلی' : 'تاریخچه'}
                  </Badge>
                  {v.source === 'CORRECTION'
                    ? 'اصلاح'
                    : v.source === 'ADMIN_CORRECTION'
                      ? 'اصلاح مدیر'
                      : 'احراز سطح ۱'}{' '}
                  · {dt(v.verifiedAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="شواهد استعلام‌ها" className="xl:col-span-2">
        <DataTable
          label="شواهد استعلام‌ها"
          rows={d.evidence}
          density="compact"
          stickyFirst
          columns={[
            {
              id: 'type',
              header: 'بررسی',
              isRowHeader: true,
              cell: (e) => (
                <Labeled label={EVIDENCE_TYPE[e.type] ?? 'بررسی ثبت‌شده'} code={e.type} />
              ),
            },
            {
              id: 'outcome',
              header: 'نتیجه',
              cell: (e) => {
                const o = outcomeLabel(e.outcome);
                return (
                  <span className="flex flex-col items-start gap-0.5">
                    <Badge tone={o.tone}>{o.label}</Badge>
                    <TechCode>
                      {e.outcome}
                      {e.category ? ` · ${e.category}` : ''}
                    </TechCode>
                  </span>
                );
              },
            },
            { id: 'at', header: 'زمان', cell: (e) => dt(e.requestedAt) },
            {
              id: 'tech',
              header: 'جزئیات فنی',
              cell: (e) => (
                <details className="text-[12px]" data-evidence-tech>
                  <summary className="cursor-pointer text-fg-muted">نمایش</summary>
                  <TechCode>provider: {e.provider}</TechCode>
                  <TechCode>ref: {e.providerRef ?? '—'}</TechCode>
                  <TechCode>
                    attempt: {e.attempt} · identity v{e.identityVersion}
                  </TechCode>
                </details>
              ),
            },
          ]}
          empty={<StateView compact kind="empty" title="استعلامی انجام نشده است" />}
        />
      </Section>
      <Section title="درخواست‌های احراز سطح ۲" className="xl:col-span-2" data-kyc2-section>
        <Kyc2Attempts userId={d.user.id} items={d.kyc2Submissions} />
      </Section>
      <Section title="پرونده‌های اصلاح" className="xl:col-span-2" data-cases>
        {d.corrections.length === 0 ? (
          <p className="m-0 text-[13px] text-fg-muted">پرونده‌ای وجود ندارد.</p>
        ) : (
          <ul className="m-0 list-none divide-y divide-line p-0 text-[13px]">
            {d.corrections.map((c) => (
              <li key={c.id} className="flex flex-wrap justify-between gap-2 py-2">
                <span>اصلاح هویت (از نسخه‌ی {c.baseVersion})</span>
                <span className="flex items-center gap-2 text-fg-muted">
                  <Labeled label={CORRECTION_STATUS[c.status] ?? 'وضعیت ثبت‌شده'} code={c.status} />
                  · {dt(c.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function BankingTab({ d, onChanged }: { d: AdminUserDetail; onChanged: () => void }) {
  const a = buildActions(d);
  type Row = AdminUserDetail['bankInstruments'][number];
  return (
    <Section title="کارت‌ها و شباها" data-tab-banking>
      <DataTable<Row>
        label="کارت‌ها و شباها"
        rows={d.bankInstruments}
        density="compact"
        stickyFirst
        columns={[
          {
            id: 'masked',
            header: 'شماره',
            isRowHeader: true,
            // Masked by default; the Full Admin can reveal the complete value (audited, OD-41).
            cell: (b) => (
              <InstrumentReveal userId={d.user.id} instrumentId={b.id} masked={b.masked} />
            ),
          },
          { id: 'kind', header: 'نوع', cell: (b) => (b.kind === 'CARD' ? 'کارت' : 'شبا') },
          {
            id: 'bank',
            header: 'بانک',
            cell: (b) => <BankName code={b.bankCode} />,
          },
          {
            id: 'own',
            header: 'وضعیت مالکیت',
            cell: (b) => (
              <Badge
                tone={
                  b.ownershipStatus === 'VERIFIED'
                    ? 'positive'
                    : b.ownershipStatus === 'UNAVAILABLE'
                      ? 'accent'
                      : 'negative'
                }
              >
                {OWNERSHIP[b.ownershipStatus] ?? b.ownershipStatus}
              </Badge>
            ),
          },
          { id: 'src', header: 'منبع', cell: (b) => SOURCE_LABEL(b.ownershipSource) },
          { id: 'verified', header: 'تأیید', cell: (b) => dt(b.verifiedAt) },
          { id: 'active', header: 'وضعیت', cell: (b) => (b.active ? 'فعال' : 'غیرفعال') },
          { id: 'used', header: 'آخرین استفاده', cell: (b) => dt(b.lastUsedAt) },
          {
            id: 'actions',
            header: 'عملیات',
            cell: (b) => (
              <AdminActionMenu
                d={d}
                onChanged={onChanged}
                label="عملیات"
                variant="secondary"
                size="sm"
                sections={[{ title: 'کارت / شبا', specs: a.bank(b) }]}
              />
            ),
          },
        ]}
        empty={<StateView compact kind="empty" title="کارت یا شبایی ثبت نشده است" />}
      />
    </Section>
  );
}

function SecurityTab({ d, onChanged }: { d: AdminUserDetail; onChanged: () => void }) {
  const a = buildActions(d);
  type S = AdminUserDetail['sessions'][number];
  return (
    <div className="grid gap-4 xl:grid-cols-3" data-tab-security>
      <Section
        title="امنیت حساب"
        className="xl:col-span-3"
        action={
          <AdminActionMenu
            d={d}
            onChanged={onChanged}
            label="عملیات امنیت"
            variant="secondary"
            size="sm"
            data-security-menu
            sections={[
              {
                title: 'ورود دومرحله‌ای',
                specs: a.security.filter((s) => s.key.startsWith('TOTP')),
              },
              { title: 'نشست‌ها', specs: a.security.filter((s) => s.key.startsWith('REVOKE')) },
              { title: 'حساب', specs: a.account },
            ]}
          />
        }
      >
        <KeyValueGrid
          columns={4}
          items={[
            {
              label: 'ورود دومرحله‌ای',
              value: d.security.totpEnabled ? `فعال از ${dt(d.security.totpEnabledAt)}` : 'غیرفعال',
            },
            { label: 'الزام مدیر', value: d.security.totpRequired ? 'بله' : 'خیر' },
            {
              label: 'کدهای بازیابی باقی‌مانده',
              value: <bdi className="hx-num">{d.security.recoveryCodesRemaining}</bdi>,
            },
            {
              label: 'نشست‌های فعال',
              value: <bdi className="hx-num">{d.sessions.filter((s) => !s.revokedAt).length}</bdi>,
            },
            { label: 'آخرین تغییر رمز', value: dt(d.security.passwordChangedAt) },
            { label: 'آخرین ورود', value: dt(d.security.lastLoginAt) },
            { label: 'وضعیت حساب', value: ACCOUNT_STATUS[d.user.status] ?? d.user.status },
          ]}
        />
        <p className="m-0 mt-3 text-[12px] text-fg-subtle">
          کلید ورود دومرحله‌ای کاربر هرگز نمایش داده نمی‌شود.
        </p>
      </Section>
      <Section title="نشست‌ها و دستگاه‌ها" className="xl:col-span-2">
        <DataTable<S>
          label="نشست‌ها"
          rows={d.sessions}
          density="compact"
          stickyFirst
          columns={[
            {
              id: 'device',
              header: 'دستگاه',
              isRowHeader: true,
              cell: (s) => s.device ?? 'نامشخص',
            },
            {
              id: 'ip',
              header: 'IP',
              cell: (s) => (
                <bdi dir="ltr" className="font-mono text-[12px]">
                  {s.ipPrefix ?? '—'}
                </bdi>
              ),
            },
            {
              id: 'level',
              header: 'سطح ورود',
              cell: (s) => (s.authLevel === 'MFA' ? 'دومرحله‌ای' : 'رمز عبور'),
            },
            { id: 'seen', header: 'آخرین فعالیت', cell: (s) => dt(s.lastSeenAt) },
            {
              id: 'state',
              header: 'وضعیت',
              cell: (s) => (s.revokedAt ? `بسته شده` : <Badge tone="positive">فعال</Badge>),
            },
          ]}
          empty={<StateView compact kind="empty" title="نشستی وجود ندارد" />}
        />
      </Section>
      <Section title="رویدادهای امنیتی">
        {d.securityEvents.length === 0 ? (
          <p className="m-0 text-[13px] text-fg-muted">رویدادی ثبت نشده است.</p>
        ) : (
          <ul className="m-0 list-none divide-y divide-line p-0 text-[13px]">
            {d.securityEvents.slice(0, 15).map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-2 py-2">
                <span>
                  {eventLabel(e.type)}
                  {e.severity !== 'INFO' && (
                    <span className="ms-1.5">
                      <Badge tone={SEVERITY[e.severity]?.tone ?? 'accent'}>
                        {SEVERITY[e.severity]?.label ?? e.severity}
                      </Badge>
                    </span>
                  )}
                </span>
                <span className="text-[12px] text-fg-muted">{formatRelative(e.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function HistoryTab({ d }: { d: AdminUserDetail }) {
  return (
    <div className="grid gap-4" data-tab-history>
      <Section title="تصمیم‌های مدیریتی" data-admin-decisions>
        {d.decisions.length === 0 ? (
          <p className="m-0 text-[13px] text-fg-muted">تصمیمی ثبت نشده است.</p>
        ) : (
          <ol className="m-0 list-none p-0">
            {d.decisions.map((x) => (
              <li key={x.id} className="relative border-s border-line pb-4 ps-5 last:pb-0">
                <span
                  aria-hidden
                  className="absolute -start-[5px] top-1.5 size-2.5 rounded-full bg-accent"
                />
                <p className="m-0 text-[13.5px] font-semibold">{auditLabel(x.action)}</p>
                <p className="m-0 mt-0.5 text-[12.5px] text-fg-muted">
                  دلیل: {x.reason} · مدیر: <bdi dir="ltr">{x.admin}</bdi> · {dt(x.at)}
                </p>
                <p className="m-0 mt-0.5 text-[11px] text-fg-subtle">
                  <bdi dir="ltr" className="font-mono">
                    {x.action}
                  </bdi>
                </p>
              </li>
            ))}
          </ol>
        )}
      </Section>
      <Section title="رد ممیزی این کاربر">
        <DataTable
          label="رد ممیزی کاربر"
          rows={d.audit}
          density="compact"
          stickyFirst
          maxHeight="480px"
          columns={[
            {
              id: 'action',
              header: 'رویداد',
              isRowHeader: true,
              cell: (a) => (
                <span className="flex flex-col">
                  <span>{auditLabel(a.action)}</span>
                  <bdi dir="ltr" className="font-mono text-[11px] text-fg-subtle">
                    {a.action}
                  </bdi>
                </span>
              ),
            },
            { id: 'actor', header: 'انجام‌دهنده', cell: (a) => ACTOR(a.actorType) },
            { id: 'reason', header: 'دلیل', cell: (a) => a.reason ?? '—' },
            { id: 'at', header: 'زمان', cell: (a) => dt(a.at) },
          ]}
          empty={<StateView compact kind="empty" title="رویدادی ثبت نشده است" />}
        />
      </Section>
    </div>
  );
}

/* ───────── page ───────── */

export function Customer360Page({
  id,
  tab,
  onTab,
}: {
  id: string;
  tab: string | undefined;
  onTab: (t: string) => void;
}) {
  const r = useRemote<AdminUserDetail>(`/users/${id}`);
  return (
    <AppContainer>
      <Remote r={r}>
        {(d) => <Customer360 d={d} tab={tab} onTab={onTab} reload={r.reload} />}
      </Remote>
    </AppContainer>
  );
}

function Customer360({
  d,
  tab,
  onTab,
  reload,
}: {
  d: AdminUserDetail;
  tab: string | undefined;
  onTab: (t: string) => void;
  reload: () => void;
}) {
  const f = useCustomerFinance(d.user.id, d.financial);
  const tx = f.transactions ?? [];
  const userLabel = `${d.user.displayName ?? 'بدون نام'} (${d.user.publicId})`;
  const refresh = () => {
    f.reload();
    reload();
  };
  const walletsTab: PageTab = {
    id: 'wallets',
    label: 'کیف پول',
    content: <WalletTab f={f} />,
  };
  const allTx: PageTab = {
    id: 'transactions',
    label: 'تراکنش‌ها',
    content: (
      <Section title="همه‌ی تراکنش‌ها" data-tab-transactions>
        <TxTable rows={tx} label="همه‌ی تراکنش‌ها" />
      </Section>
    ),
  };
  // LEDGER (Phase 4): real money tabs. Crypto and trade tabs arrive with Phases 5–6 — no fake rows.
  // LEDGER (Phase 4): the tab set is fixed by the server's `financial.source` from the first render
  // (a stable tablist for keyboard users); only the contents wait for the ledger read model, with an
  // explicit loading or error state — never an empty table or a zero.
  const pending = f.error ? (
    <Card className="p-6">
      <StateView
        kind="error"
        title="اطلاعات مالی بارگذاری نشد"
        description={f.error}
        action={
          <button
            type="button"
            onClick={f.reload}
            className="h-10 cursor-pointer rounded-md border border-line-strong bg-surface-2 px-4 text-[13px] font-semibold hover:bg-surface-3"
          >
            تلاش دوباره
          </button>
        }
      />
    </Card>
  ) : (
    <Card className="h-56 animate-pulse" aria-busy="true" />
  );
  const withLedger = (render: (l: NonNullable<typeof f.ledger>) => ReactNode) =>
    f.ledger ? render(f.ledger) : pending;
  const ledgerTabs: PageTab[] =
    d.financial.source === 'LEDGER' && !f.fixture
      ? [
          { ...walletsTab, content: withLedger(() => walletsTab.content) },
          { ...allTx, content: withLedger(() => allTx.content) },
          {
            id: 'money-ops',
            label: 'عملیات مالی',
            content: withLedger((l) => (
              <MoneyOpsTab userId={d.user.id} userLabel={userLabel} l={l} onChanged={refresh} />
            )),
          },
          { id: 'toman', label: 'تومان', content: withLedger((l) => <TomanTab l={l} />) },
          // Phase 8: later-phase truth in the customer file (read-only; actions stay on their queues).
          { id: 'crypto', label: 'رمزارز', content: <CryptoTab userId={d.user.id} /> },
          { id: 'trades', label: 'خرید و فروش', content: <TradesTab userId={d.user.id} /> },
          { id: 'support', label: 'پشتیبانی', content: <SupportTab userId={d.user.id} /> },
          {
            id: 'journal',
            label: 'دفتر کل',
            content: withLedger((l) => (
              <Section title="اسناد دفتر کل این کاربر" data-tab-journal>
                <JournalList rows={l.journal} userId={d.user.id} />
              </Section>
            )),
          },
        ]
      : [];
  // Dev-only REVIEW fixture keeps the full later-phase layout for design review.
  const fixtureTabs: PageTab[] =
    f.fixture && f.wallets !== null && f.transactions !== null
      ? [
          walletsTab,
          allTx,
          {
            id: 'toman',
            label: 'تومان',
            content: (
              <Section title="واریز و برداشت تومان" data-tab-toman>
                <TxTable
                  rows={tx.filter((t) => t.type.startsWith('TOMAN'))}
                  label="تراکنش‌های تومان"
                />
              </Section>
            ),
          },
          {
            id: 'crypto',
            label: 'رمزارز',
            content: (
              <Section title="واریز و برداشت رمزارز" data-tab-crypto>
                <TxTable
                  rows={tx.filter((t) => t.type.startsWith('CRYPTO'))}
                  label="تراکنش‌های رمزارز"
                />
              </Section>
            ),
          },
          {
            id: 'trades',
            label: 'خرید / فروش / تبدیل',
            content: (
              <Section title="خرید، فروش و تبدیل" data-tab-trades>
                <TxTable
                  rows={tx.filter(
                    (t) => t.type === 'BUY' || t.type === 'SELL' || t.type === 'CONVERT',
                  )}
                  label="معاملات"
                />
              </Section>
            ),
          },
        ]
      : [];
  const tabs: PageTab[] = [
    { id: 'overview', label: 'نمای کلی', content: <OverviewTab d={d} f={f} /> },
    ...ledgerTabs,
    ...fixtureTabs,
    { id: 'kyc', label: 'احراز هویت', content: <KycTab d={d} onChanged={reload} /> },
    {
      id: 'banking',
      label: 'حساب‌های بانکی',
      badge:
        d.bankInstruments.length > 0 ? (
          <Badge>{d.bankInstruments.length.toLocaleString('fa-IR')}</Badge>
        ) : undefined,
      content: <BankingTab d={d} onChanged={reload} />,
    },
    { id: 'security', label: 'امنیت', content: <SecurityTab d={d} onChanged={reload} /> },
    {
      id: 'history',
      label: 'تاریخچه مدیریتی',
      badge:
        d.decisions.length > 0 ? (
          <Badge>{d.decisions.length.toLocaleString('fa-IR')}</Badge>
        ) : undefined,
      content: <HistoryTab d={d} />,
    },
  ];
  const selected = tabs.some((t) => t.id === tab) ? (tab as string) : 'overview';
  return (
    <>
      <Header d={d} f={f} onChanged={refresh} />
      <PageTabs label="بخش‌های پرونده‌ی مشتری" items={tabs} selected={selected} onSelect={onTab} />
    </>
  );
}
